/**
 * useLocale — single source of truth for locale-aware formatting.
 *
 * Subscribes to `i18n.language` and the cached user preferences so components
 * re-render on language switch OR number-format preference change.
 * Returns `lang`, `isRTL`, `locale` plus `fmtNum`/`fmtDate` bound to current lang.
 */
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { settingsKeys } from "@/shared/lib/queryKeys";
import { formatDate, formatNum, resolveNumLocale, toArabicNumerals } from "@/shared/lib/utils";

export function useLocale() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const isRTL = lang === "ar";

  // Read numberFormat from the preferences cache without triggering a fetch.
  // When useSettings updates the cache, this hook re-renders automatically.
  const { data: prefs } = useQuery<{ numberFormat?: "western" | "arabic" }>({
    queryKey: settingsKeys.preferences(),
    enabled: false,
    staleTime: Infinity,
  });
  const numberFormat = prefs?.numberFormat ?? "western";

  // Digit glyphs follow the preference, base locale follows language — so numbers
  // are independent of language. See resolveNumLocale for the full rationale.
  const numLocale = resolveNumLocale(lang, numberFormat);

  return {
    lang,
    isRTL,
    /**
     * Preference-aware locale for ALL number/date formatting (incl. currency,
     * relative time). Pass this to shared pure formatters — never a bare `lang`.
     */
    locale: numLocale,
    numLocale,
    fmtNum: (n: number, opts?: Intl.NumberFormatOptions) => formatNum(n, numLocale, opts),
    fmtDate: (d: Date | string, opts?: Intl.DateTimeFormatOptions) =>
      formatDate(d, numLocale, opts),
    /** Localize digits inside an already-formatted string per the user's number-format preference. */
    fmtDigits: (s: string) => (numberFormat === "arabic" ? toArabicNumerals(s) : s),
  };
}
